// Shared helpers for the rich-text "Insert link" popover: find the <a> under a
// selection and stamp or clear target="_blank" after createLink. The server
// sanitizer allowlists href/target/rel and adds rel="noopener" on save; the
// editor mirrors that so a new-tab choice is visible before autosave lands.

export const LINK_ATTRIBUTE_ALLOWLIST = ['href', 'target', 'rel']
export const TABLE_EDITOR_SELECTOR = '[data-table-cell-editor], [data-table-header-editor]'

export function linkFromNode(node) {
  if (!node) return null

  const element = node.nodeType === Node.TEXT_NODE ? node.parentElement : node

  return element?.closest?.('a') ?? null
}

export function linkFromRange(range) {
  if (!range) return null

  const fromBoundary = linkWrappingRange(range)
  if (fromBoundary) return fromBoundary

  const ancestor = range.commonAncestorContainer
  const element = ancestor.nodeType === Node.ELEMENT_NODE ? ancestor : ancestor.parentElement

  return element?.querySelector?.('a') ?? null
}

export function linkWrappingRange(range) {
  if (!range) return null

  return linkFromNode(range.commonAncestorContainer) ||
    linkFromNode(range.startContainer) ||
    linkFromNode(range.endContainer)
}

export function linkFromSelection(root) {
  const selection = window.getSelection()
  if (!selection || selection.rangeCount === 0) return null

  const link = linkFromNode(selection.anchorNode) || linkFromNode(selection.focusNode)

  return link && root.contains(link) ? link : null
}

export function applyLinkTarget(link, openInNewTab) {
  if (!link) return

  if (openInNewTab) {
    link.setAttribute('target', '_blank')
    link.setAttribute('rel', 'noopener')
  } else {
    link.removeAttribute('target')
    link.removeAttribute('rel')
  }
}

export function rangeIsInRoot(range, root) {
  if (!range || !root) return false

  const ancestor = range.commonAncestorContainer

  return ancestor === root || root.contains(ancestor)
}

export function tableEditorForRange(range, tableRoot) {
  if (!range || !tableRoot) return null

  const ancestor = range.commonAncestorContainer
  const element = ancestor.nodeType === Node.TEXT_NODE ? ancestor.parentElement : ancestor
  const editor = element?.closest?.(TABLE_EDITOR_SELECTOR)

  return editor && tableRoot.contains(editor) ? editor : null
}

// Toolbar clicks collapse the live highlight (the caret moves into the button or
// URL field). Keep a previously saved, non-collapsed editor range instead of
// replacing it with that collapsed caret — that's the range Apply still needs.
export function cloneLiveRange(existingRange, rangeBelongs) {
  const selection = window.getSelection()
  if (!selection || selection.rangeCount === 0) return existingRange

  const range = selection.getRangeAt(0)
  if (!rangeBelongs(range)) return existingRange
  if (range.collapsed && existingRange && !existingRange.collapsed) return existingRange

  return range.cloneRange()
}

export function applyLinkToRange(range, editor, url, openInNewTab) {
  if (!range || !editor || !url) return null

  const existing = linkWrappingRange(range)
  if (existing && editor.contains(existing)) {
    existing.setAttribute('href', url)
    applyLinkTarget(existing, openInNewTab)
    return existing
  }

  try {
    editor.focus()
    const selection = window.getSelection()
    selection.removeAllRanges()
    selection.addRange(range)
    document.execCommand('createLink', false, url)
  } catch (_error) {
    return wrapRangeContents(range, url, openInNewTab)
  }

  const created = linkFromSelection(editor)
  if (created) {
    applyLinkTarget(created, openInNewTab)
    return created
  }

  return wrapRangeContents(range, url, openInNewTab)
}

function wrapRangeContents(range, url, openInNewTab) {
  if (!range || range.collapsed) return null

  const link = document.createElement('a')
  link.setAttribute('href', url)
  applyLinkTarget(link, openInNewTab)

  try {
    range.surroundContents(link)
  } catch (_error) {
    link.appendChild(range.extractContents())
    range.insertNode(link)
  }

  return link
}
